import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { LegalShell } from './PrivacyPage';

interface Status {
  code: string;
  status: 'PENDING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  completedAt: string | null;
}

export default function DataDeletionStatusPage() {
  const { code } = useParams<{ code: string }>();
  const [data, setData] = useState<Status | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!code) return;
    fetch(`/api/data-deletion/${encodeURIComponent(code)}`)
      .then(async r => {
        if (!r.ok) throw new Error(r.status === 404 ? 'No deletion request found with this code.' : 'Could not load status');
        return r.json();
      })
      .then(setData)
      .catch(e => setErr(e.message));
  }, [code]);

  return (
    <LegalShell title="Data deletion status">
      {!code && <p className="text-rose-400">Missing code.</p>}
      {err && <p className="text-rose-400">{err}</p>}
      {data && (
        <>
          <p>
            Confirmation code:{' '}
            <span className="font-mono text-accent">{data.code}</span>
          </p>
          <p>
            Status:{' '}
            <span className={
              data.status === 'COMPLETED' ? 'text-emerald-400 font-semibold' :
              data.status === 'FAILED' ? 'text-rose-400 font-semibold' :
              'text-ink-200 font-semibold'
            }>
              {data.status}
            </span>
          </p>
          <p className="text-ink-300 text-sm">
            Requested: {new Date(data.createdAt).toLocaleString()}
          </p>
          {data.completedAt && (
            <p className="text-ink-300 text-sm">
              Completed: {new Date(data.completedAt).toLocaleString()}
            </p>
          )}
          {data.status === 'COMPLETED' && (
            <p className="mt-4">
              Your Durak account and all associated game data have been permanently deleted. You can play again
              as a new user any time by signing in.
            </p>
          )}
          {data.status === 'FAILED' && (
            <p className="mt-4">
              We couldn't complete the deletion automatically. Please email{' '}
              <a className="text-accent underline" href="mailto:admin@durak.runnable.dev">admin@durak.runnable.dev</a>
              {' '}and we'll handle it within 30 days.
            </p>
          )}
        </>
      )}
      {!data && !err && <p className="animate-pulse text-ink-200">Loading…</p>}
    </LegalShell>
  );
}
